import { useCallback, useEffect, useState } from "react";
import { LuArrowUpRight, LuCheck, LuMail, LuSend } from "react-icons/lu";
import {
	Avatar,
	Badge,
	Box,
	Container,
	HStack,
	IconButton,
	Separator,
	Spacer,
	Stack,
	Text,
	Tooltip,
} from "@chakra-ui/react";
import avatar from "../assets/avatar.jpg";
import { ColorModeButton } from "../components/ui/color-mode";

const PROFILE = {
	name: "Balazs Fabian",
	email: "balazsfabian@tuta.com",
	telegram: "https://t.me/misterfabian",
	avatar: avatar,
};

const EXPERIENCE = [
	{
		acquired: true,
		company: "Clawdoe",
		years: "2025-2026",
		// url: "https://clawdoe.com",
		url: "https://www.npmjs.com/package/clawdoe",
		role: "Co-founder and chief technology officer",
		blurb: "Payments infrastructure and data layer for agentic commerce",
		detail: "Co-founded and lead technical direction from the ground up of agentic payments platform, including human-facing dashboard, developer-facing NPM module, and agent-facing SDK and MCP server. Built overall system architecture and scaled from 0 to hundreds of users.",
	},
	{
		company: "1024 Holdings",
		years: "2024-2025",
		url: "https://1024holdings.com",
		role: "Head of technical product development",
		blurb: "Quantitative finance and asset management",
		detail: "Led business and product direction of new vertical from classical macro-oriented asset management to software-driven quantitative portfolio automations. Built and scaled team from 2 to 11.",
	},
	{
		company: "Postmodern Protocol",
		years: "2021-2022",
		url: "https://algorand.co",
		role: "Founder and chief product officer",
		blurb: "Algorithmic stablecoin system in partnership with the Algorand Foundation",
		detail: "Founded and led product direction at the first native stablecoin on the Algorand blockchain. Oversaw development from the ground up and scaled to hundreds of users and hundreds of thousands in total value locked. Led business development with ecosystem partners along with the Algorand foundation.",
	},
	{
		company: "Geneva Chat",
		years: "2019-2020",
		acquired: true,
		url: "https://ir.bumble.com/news/news-details/2024/Bumble-Inc.-Signs-Agreement-to-Acquire-Group-and-Community-App-Geneva/default.aspx",
		role: "Head of mobile engineering team",
		blurb: "Consumer-facing community messaging platform",
		detail: "First outside hire after founding team. Oversaw development of iOS and Android client applications from the ground up. Built initial architecture of the iOS client while scaling from 0 to thousands of users. Built and managed mobile development team from 0 to 5.",
	},
	{
		company: "Mello",
		years: "2018-2019",
		acquired: true,
		role: "iOS development lead",
		blurb: "Realtime video sharing startup",
		detail: "First technical hire. Built the initial iOS client from the ground up until release. Built and managed the iOS team from 0 to 3, scaled from scratch through to acquisition.",
	},
];

const SKILLS = [
	{
		color: "blue",
		items: ["JavaScript", "TypeScript", "HTML", "CSS", "React", "Svelte", "Node.js", "Next.js", "Python"],
	},
	{ color: "green", items: ["GCP", "Firebase", "AWS", "Vercel", "Supabase", "Postgres", "Stripe"] },
	{ color: "purple", items: ["iOS", "MacOS", "Swift", "Xcode", "UIKit", "SwiftUI", "Vapor", "Combine", "CloudKit"] },
	{ color: "red", items: ["C++", "Arduino", "Rust"] },
	{ color: "orange", items: ["Bitcoin", "Ethereum", "Solidity", "Solana"] },
];

const EDUCATION = [
	{ school: "University of Edinburgh", degree: "Financial Mathematics" },
	{
		school: "New York University",
		degree: "Computer Science and Finance",
	},
];

// The clock reads in Zurich time so it matches the label beside it.
const ZONE = "Europe/Zurich";

/* The async Clipboard API needs focus and a secure context; fall back to the legacy path. */
async function writeToClipboard(text) {
	try {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// Rejected — try the textarea path below.
	}
	try {
		const el = document.createElement("textarea");
		el.value = text;
		el.setAttribute("readonly", "");
		el.style.position = "fixed";
		el.style.opacity = "0";
		document.body.appendChild(el);
		el.select();
		const ok = document.execCommand("copy");
		document.body.removeChild(el);
		return ok;
	} catch {
		return false;
	}
}

function StatusDot({ color, pulse }) {
	return (
		<Box position="relative" display="inline-flex" w="1" h="1" flexShrink="0">
			{pulse && <Box position="absolute" inset="0" borderRadius="full" bg={color} className="pulse-ping" />}
			<Box position="relative" w="full" h="full" borderRadius="full" bg={color} />
		</Box>
	);
}

/* Online hours are 8am-6pm in ZONE, checked against the live clock. */
function isOnlineHours(date) {
	const hour = Number(
		new Intl.DateTimeFormat("en-GB", { timeZone: ZONE, hour: "2-digit", hourCycle: "h23" }).format(date),
	);
	return hour >= 8 && hour < 18;
}

function useClock() {
	const [now, setNow] = useState(() => new Date());
	useEffect(() => {
		const id = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(id);
	}, []);
	return now;
}

function Role({ title, years, url, role, blurb, detail, hoverable, acquired }) {
	const titleContent = (
		<HStack gap="2">
			<Text color="fg.muted">{title}</Text>
			{acquired && (
				<Badge size="xs" colorPalette="yellow" variant="subtle" borderRadius="full" px="2" color="yellow.600">
					acquired
				</Badge>
			)}
		</HStack>
	);

	const boxProps = {
		gap: "0",
		mx: "-4",
		px: "5",
		py: "4",
		borderRadius: "lg",
		transition: "background-color 1s ease",
		_hover: hoverable ? { bg: "bg.muted" } : undefined,
	};

	const content = (
		<>
			{years ? (
				<>
					<HStack justify="space-between" gap="4">
						<Text color="fg.subtle" fontSize="xs">
							{years}
						</Text>
						{url && (
							<Text as="span" color="fg.subtle" _groupHover={{ color: "fg" }}>
								<LuArrowUpRight />
							</Text>
						)}
					</HStack>
					{titleContent}
				</>
			) : (
				<HStack justify="space-between" gap="4">
					{titleContent}
					{url && (
						<Text as="span" color="fg.subtle" _groupHover={{ color: "fg.muted" }}>
							<LuArrowUpRight />
						</Text>
					)}
				</HStack>
			)}
			<Text color="fg" fontWeight="medium">
				{role}
			</Text>
			{blurb && <Text color="fg.subtle">{blurb}</Text>}
			{detail && (
				<Box
					display="grid"
					gridTemplateRows="0fr"
					_groupHover={{ gridTemplateRows: "1fr" }}
					transition="grid-template-rows 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
				>
					<Box overflow="hidden" minH="0">
						<Text
							color="fg.muted"
							fontSize="sm"
							pt="2"
							opacity="0"
							_groupHover={{ opacity: 1 }}
							transition="opacity 1s ease"
						>
							{detail}
						</Text>
					</Box>
				</Box>
			)}
		</>
	);

	if (url) {
		return (
			<Stack asChild {...boxProps}>
				<a href={url} target="_blank" rel="noreferrer noopener" className="group">
					{content}
				</a>
			</Stack>
		);
	}

	return (
		<Stack {...boxProps} className="group">
			{content}
		</Stack>
	);
}

export default function Home() {
	const now = useClock();
	const [copied, setCopied] = useState(false);
	const isOnline = isOnlineHours(now);

	const copyEmail = useCallback(async () => {
		if (await writeToClipboard(PROFILE.email)) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
			return;
		}
		window.location.href = `mailto:${PROFILE.email}`;
	}, []);

	const time = now.toLocaleTimeString("en-GB", {
		timeZone: ZONE,
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
	const weekday = now.toLocaleDateString("en-GB", { timeZone: ZONE, weekday: "long" });
	const date = now.toLocaleDateString("en-GB", {
		timeZone: ZONE,
		day: "numeric",
		month: "long",
		year: "numeric",
	});

	return (
		<Container maxW="3xl" py="12">
			<ColorModeButton
				position="fixed"
				top={{ base: "auto", md: "4" }}
				bottom={{ base: "4", md: "auto" }}
				right="4"
			/>
			<Stack gap="12">
				{/* HEADER */}
				<HStack
					asChild
					justify="space-between"
					align="center"
					textStyle="xs"
					fontWeight="medium"
					letterSpacing={1}
				>
					<Stack>
						<Stack gap="0">
							<Text color="fg.muted">{weekday.toUpperCase()}</Text>
							<Text color="fg.subtle">{date.toUpperCase()}</Text>
						</Stack>
						<Stack gap="0" align="end">
							<Text color="fg.muted">{time}</Text>
							<Text color="fg.subtle">ZÜRICH | GMT+2</Text>
						</Stack>
					</Stack>
				</HStack>

				<Separator />
				{/* PROFILE */}

				<Stack gap="6" pb={4}>
					<HStack>
						<HStack gap={4}>
							<Tooltip.Root openDelay={0} closeDelay={0}>
								<Tooltip.Trigger asChild>
									<Box position="relative" display="inline-flex" cursor="default">
										<Avatar.Root size="2xl">
											<Avatar.Fallback name={PROFILE.name} />
											<Avatar.Image src={PROFILE.avatar} alt={PROFILE.name} />
										</Avatar.Root>
										<Badge
											position="absolute"
											bottom="0"
											left="50%"
											transform="translateX(-50%)"
											size="xs"
											borderRadius="full"
											colorPalette={isOnline ? "green" : "red"}
											variant="subtle"
											px="2"
											gap="1"
										>
											<StatusDot color={isOnline ? "green.400" : "red.400"} pulse />
											{isOnline ? "online" : "offline"}
										</Badge>
									</Box>
								</Tooltip.Trigger>
								<Tooltip.Positioner>
									<Tooltip.Content>
										{isOnline ? "Hit me up!" : "I'll get back to you tomorrow."}
									</Tooltip.Content>
								</Tooltip.Positioner>
							</Tooltip.Root>
							<Text color="fg" fontWeight="medium">
								{PROFILE.name}
							</Text>
						</HStack>

						<Spacer />

						<HStack gap={2} flexWrap="wrap">
							<Tooltip.Root openDelay={0} closeDelay={0} closeOnClick={false} closeOnPointerDown={false}>
								<Tooltip.Trigger asChild>
									<IconButton size="sm" variant="outline" borderRadius="full" onClick={copyEmail}>
										{copied ? <LuCheck /> : <LuMail />}
									</IconButton>
								</Tooltip.Trigger>
								<Tooltip.Positioner>
									<Tooltip.Content>{copied ? "Copied" : "Email"}</Tooltip.Content>
								</Tooltip.Positioner>
							</Tooltip.Root>
							<Tooltip.Root openDelay={0} closeDelay={0}>
								<Tooltip.Trigger asChild>
									<IconButton
										size="sm"
										variant="outline"
										borderRadius="full"
										onClick={() => window.open(PROFILE.telegram, "_blank", "noopener,noreferrer")}
									>
										<LuSend />
									</IconButton>
								</Tooltip.Trigger>
								<Tooltip.Positioner>
									<Tooltip.Content>Telegram</Tooltip.Content>
								</Tooltip.Positioner>
							</Tooltip.Root>
						</HStack>
					</HStack>

					<Text color="fg.muted">
						I build at the intersection of technology and finance, with experience across nearly every
						domain of software engineering and a focus on product development from the ground up.
					</Text>

					<HStack gap="2" flexWrap="wrap">
						{SKILLS.flatMap((group) =>
							group.items.map((item) => (
								<Badge
									key={item}
									colorPalette={group.color}
									variant="subtle"
									borderRadius="full"
									px="3"
									color={`${group.color}.600`}
								>
									{item}
								</Badge>
							)),
						)}
					</HStack>
				</Stack>

				<Separator />
				{/* LIST */}

				<Stack>
					{EXPERIENCE.map((item) => (
						<Role
							key={item.company}
							title={item.company}
							years={item.years}
							url={item.url}
							role={item.role}
							blurb={item.blurb}
							detail={item.detail}
							acquired={item.acquired}
							hoverable
						/>
					))}
					{EDUCATION.map((item) => (
						<Role key={item.school} role={item.school} blurb={item.degree} />
					))}
				</Stack>

				<Separator />
				{/* FOOTER */}

				<HStack justify="flex-end">
					<Text textStyle="xs" color="fg.subtle">
						{new Date().getFullYear()} © {PROFILE.name} — All rights reserved.
					</Text>
				</HStack>
			</Stack>
		</Container>
	);
}
